import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { FixedAssetsEndpoints } from './fixedAssetsEndpoints';
import {
    gelNumberOf,
    mapAssetCategories,
    mapCommitDepreciationRun,
    mapCommitDisposal,
    mapCreateFixedAsset,
    mapFixedAssetDetail,
    mapFixedAssetList,
    mapPreviewDepreciationRun,
    mapPreviewDisposal,
    mapReconciliation,
    mapUpdateFixedAsset,
} from './fixedAssetsMapping';
import type { AssetsById, IFixedAssetsService } from './IFixedAssetsService';
import type {
    CreateFixedAssetRequest,
    DepreciationRunRequest,
    DisposalRequest,
    FixedAssetListQuery,
    UpdateFixedAssetRequest,
} from './requests';
import type {
    CommitDepreciationRunResponse,
    CommitDisposalResponse,
    CreateFixedAssetResponse,
    FixedAssetReconciliation,
    GetAssetCategoriesResponse,
    GetFixedAssetResponse,
    GetFixedAssetsResponse,
    PreviewDepreciationRunResponse,
    PreviewDisposalResponse,
    UpdateFixedAssetResponse,
} from './responses';
import type { LocaleCode } from '@/api/models';
import { parseGelToTetri } from '@/utils/money';

type FixedAssetsServiceOptions = {
    baseUrl: string;
};

/**
 * Decimal GEL from the form → the JSON number the write edge takes.
 *
 * Routed through `parseGelToTetri` rather than `Number(value)` so the crossing
 * happens on the integer, exactly once, as it does everywhere else in this
 * client. A blank field is `0` — the forms use `""` for "nil salvage" — and an
 * unparseable one is `0` too, which the server then refuses on its own terms
 * (`COST_NOT_POSITIVE`) rather than the client inventing a message for it.
 */
const gelFieldToNumber = (value: string): number => {
    const parsed = parseGelToTetri(value);
    return gelNumberOf(parsed.ok ? parsed.tetri : 0);
};

/**
 * Real HTTP Fixed Assets client — the register bound to `bos-api`.
 *
 * Every method maps its response through `fixedAssetsMapping`, which is the only
 * file that knows the wire's field names. Nothing here interprets a payload
 * beyond passing it to a mapper, and nothing here posts to the ledger: the
 * drafts these calls return are handed to the Journal Entry form, where a person
 * posts them under the journal's own validation and period gating.
 *
 * **No request sends `source`** — the server stamps every entry it builds
 * (`ACQUISITION` / `DEPRECIATION` / `DISPOSAL`). It is silently accepted on
 * create rather than refused, so this is a discipline the client keeps, not one
 * the API enforces.
 *
 * **No request sends `locale` to the register**, which never honoured it. The
 * three draft-building endpoints and the reconciliation do take one, and it is
 * always sent explicitly because the server's default is `ka` regardless of the
 * app's language.
 */
export class FixedAssetsService implements IFixedAssetsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: FixedAssetsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getCategories(): Promise<
        ApiResponse<GetAssetCategoriesResponse>
    > {
        const result = await makeApiRequest<unknown>(
            this.client.get(FixedAssetsEndpoints.categories()),
        );
        return result.success
            ? { success: true, response: mapAssetCategories(result.response) }
            : result;
    }

    public async getAssets(
        query: FixedAssetListQuery,
    ): Promise<ApiResponse<GetFixedAssetsResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(
                FixedAssetsEndpoints.list({
                    status: query.status,
                    search: query.search,
                }),
            ),
        );
        return result.success
            ? { success: true, response: mapFixedAssetList(result.response) }
            : result;
    }

    public async getAsset(
        assetId: string,
    ): Promise<ApiResponse<GetFixedAssetResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(FixedAssetsEndpoints.detail(assetId)),
        );
        return result.success
            ? { success: true, response: mapFixedAssetDetail(result.response) }
            : result;
    }

    /**
     * The opening fields travel **only** on an opening asset and
     * `fundingAccountId` **only** on an acquisition — the server refuses each in
     * the wrong context (`FUNDING_ACCOUNT_NOT_ALLOWED` /
     * `FUNDING_ACCOUNT_REQUIRED`), and sending a stray null would be a request
     * that says something the user did not.
     */
    public async createAsset(
        body: CreateFixedAssetRequest,
    ): Promise<ApiResponse<CreateFixedAssetResponse>> {
        const payload = {
            id: body.id,
            name: body.name,
            categoryId: body.categoryId,
            acquisitionDate: body.acquisitionDate,
            cost: gelFieldToNumber(body.cost),
            salvageValue: gelFieldToNumber(body.salvageValue),
            usefulLifeMonths: body.usefulLifeMonths,
            method: body.method,
            isOpening: body.isOpening,
            ...(body.isOpening
                ? {
                      openingAccumulatedDepreciation: gelFieldToNumber(
                          body.openingAccumulatedDepreciation ?? '',
                      ),
                      ...(body.openingDepreciatedThrough
                          ? {
                                openingDepreciatedThrough:
                                    body.openingDepreciatedThrough,
                            }
                          : {}),
                  }
                : { fundingAccountId: body.fundingAccountId ?? null }),
            ...(body.locale ? { locale: body.locale } : {}),
        };

        const result = await makeApiRequest<unknown>(
            this.client.post(FixedAssetsEndpoints.create(), payload),
        );
        return result.success
            ? { success: true, response: mapCreateFixedAsset(result.response) }
            : result;
    }

    public async updateAsset(
        assetId: string,
        body: UpdateFixedAssetRequest,
    ): Promise<ApiResponse<UpdateFixedAssetResponse>> {
        const payload = {
            name: body.name,
            categoryId: body.categoryId,
            acquisitionDate: body.acquisitionDate,
            cost: gelFieldToNumber(body.cost),
            salvageValue: gelFieldToNumber(body.salvageValue),
            usefulLifeMonths: body.usefulLifeMonths,
            method: body.method,
        };

        const result = await makeApiRequest<unknown>(
            this.client.put(FixedAssetsEndpoints.update(assetId), payload),
        );
        return result.success
            ? { success: true, response: mapUpdateFixedAsset(result.response) }
            : result;
    }

    /**
     * `proceedsAccountId` travels only when proceeds are non-zero: the server
     * refuses it on a nil-proceeds disposal (`PROCEEDS_ACCOUNT_NOT_ALLOWED`) and
     * requires it otherwise (`PROCEEDS_ACCOUNT_REQUIRED`).
     */
    private disposalPayload(body: DisposalRequest) {
        const proceeds = gelFieldToNumber(body.proceeds);
        return {
            disposalDate: body.disposalDate,
            proceeds,
            ...(proceeds > 0 && body.proceedsAccountId
                ? { proceedsAccountId: body.proceedsAccountId }
                : {}),
            ...(body.locale ? { locale: body.locale } : {}),
        };
    }

    public async previewDisposal(
        assetId: string,
        body: DisposalRequest,
    ): Promise<ApiResponse<PreviewDisposalResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(
                FixedAssetsEndpoints.previewDisposal(assetId),
                this.disposalPayload(body),
            ),
        );
        return result.success
            ? { success: true, response: mapPreviewDisposal(result.response) }
            : result;
    }

    public async commitDisposal(
        assetId: string,
        body: DisposalRequest,
    ): Promise<ApiResponse<CommitDisposalResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(
                FixedAssetsEndpoints.commitDisposal(assetId),
                this.disposalPayload(body),
            ),
        );
        return result.success
            ? { success: true, response: mapCommitDisposal(result.response) }
            : result;
    }

    public async previewDepreciationRun(
        body: DepreciationRunRequest,
        assetsById?: AssetsById,
    ): Promise<ApiResponse<PreviewDepreciationRunResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(FixedAssetsEndpoints.previewRun(), {
                period: body.period,
                ...(body.locale ? { locale: body.locale } : {}),
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: mapPreviewDepreciationRun(
                      result.response,
                      assetsById,
                  ),
              }
            : result;
    }

    public async commitDepreciationRun(
        body: DepreciationRunRequest,
        assetsById?: AssetsById,
    ): Promise<ApiResponse<CommitDepreciationRunResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(FixedAssetsEndpoints.commitRun(), {
                period: body.period,
                ...(body.locale ? { locale: body.locale } : {}),
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: mapCommitDepreciationRun(
                      result.response,
                      assetsById,
                  ),
              }
            : result;
    }

    public async getReconciliation(
        locale: LocaleCode,
    ): Promise<ApiResponse<FixedAssetReconciliation>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(FixedAssetsEndpoints.reconciliation(locale)),
        );
        return result.success
            ? { success: true, response: mapReconciliation(result.response) }
            : result;
    }
}
